import { Link, useLocation } from 'react-router-dom';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Step {
  to: string;
  label: string;
}

interface StepNavProps {
  steps: Step[];
}

export function StepNav({ steps }: StepNavProps) {
  const location = useLocation();
  const currentIndex = steps.findIndex((s) => location.pathname === s.to);
  const activeIndex = currentIndex === -1 ? 0 : currentIndex;

  return (
    <ol className="flex flex-wrap items-center gap-2 sm:gap-4" aria-label="Progress">
      {steps.map((s, i) => {
        const completed = i < activeIndex;
        const active = i === activeIndex;
        return (
          <li key={s.to} className="flex items-center gap-2">
            <Link
              to={s.to}
              aria-current={active ? 'step' : undefined}
              className={cn(
                'flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                active && 'bg-(--color-primary) text-(--color-primary-foreground)',
                !active && completed && 'bg-(--color-accent) text-(--color-accent-foreground)',
                !active && !completed && 'bg-(--color-muted) text-(--color-muted-foreground)',
              )}
            >
              <span
                className={cn(
                  'grid h-5 w-5 place-items-center rounded-full text-[10px] font-semibold',
                  active && 'bg-white/25 text-white',
                  !active && completed && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
                  !active && !completed && 'bg-(--color-background) text-(--color-muted-foreground)',
                )}
              >
                {completed ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              {s.label}
            </Link>
            {i < steps.length - 1 ? (
              <span className="hidden h-px w-8 bg-(--color-border) sm:block" />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
